import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFirebase } from "../../context/ApiContext";
import {
	Box,
	IconButton,
	Button,
	Avatar,
	Menu,
	MenuItem,
	Typography,
	Divider,
	Badge,
	Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import AddIcon from "@mui/icons-material/Add";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import KeyboardCommandKeyIcon from "@mui/icons-material/KeyboardCommandKey";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import logoViajesMolonesWhite from "../../assets/logo-viajes-molones-white.png";
import logoViajesMolones from "../../assets/logo-viajes-molones.png";
import { useWindowSize } from "@uidotdev/usehooks";

const Topbar = ({
	user,
	onLogin,
	onSignUp,
	onLogout,
	onCreateNew,
	onMenuToggle,
	isMobile = false,
	isDashboardHome = false,
	onSearch,
	onSearchOpen,
}) => {
	const size = useWindowSize();
	const isDarkHeader = isMobile && isDashboardHome;
	const navigate = useNavigate();
	const {
		subscription,
		openCustomerPortal,
		notifications,
		unreadNotificationCount,
		dismissNotification,
		dismissAllNotifications,
	} = useFirebase();
	const [anchorEl, setAnchorEl] = useState(null);
	const [notifAnchorEl, setNotifAnchorEl] = useState(null);
	const [portalLoading, setPortalLoading] = useState(false);
	const open = Boolean(anchorEl);
	const notifOpen = Boolean(notifAnchorEl);
	const isPro = subscription?.isComplete === true;

	// Cmd+K / Ctrl+K keyboard shortcut to open search overlay
	React.useEffect(() => {
		const handleKeyDown = (e) => {
			if ((e.metaKey || e.ctrlKey) && e.key === "k") {
				e.preventDefault();
				onSearchOpen?.();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onSearchOpen]);

	const handleMenuClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleNotifClick = (event) => {
		setNotifAnchorEl(event.currentTarget);
	};

	const handleNotifClose = () => {
		setNotifAnchorEl(null);
	};

	const handleNotificationAction = async (notification) => {
		handleNotifClose();
		if (notification.action?.path) {
			navigate(notification.action.path);
		} else if (notification.action?.handler === "portal") {
			await openCustomerPortal();
		} else if (
			notification.action?.handler === "openGuide" &&
			notification.action?.projectId &&
			user
		) {
			const userId = user.uid || user.id;
			navigate(`/usuarios/${userId}/guias/${notification.action.projectId}`);
		}
	};

	const handleNavigateToProfile = () => {
		handleMenuClose();
		navigate("/profile");
	};

	const handleNavigateToSettings = () => {
		handleMenuClose();
		navigate("/profile");
	};

	return (
		<Box
			component="header"
			sx={{
				position: isDarkHeader ? "absolute" : "sticky",
				top: 0,
				left: 0,
				right: 0,
				zIndex: 1000,
				backgroundColor: isDarkHeader
					? "rgba(255, 255, 255, 0.04)"
					: "rgba(255, 255, 255, 0.8)",
				backdropFilter: isDarkHeader
					? "blur(12px)"
					: "saturate(180%) blur(20px)",
				borderBottom: isDarkHeader ? "none" : "1px solid rgba(0, 0, 0, 0.05)",
			}}
		>
			<Box
				sx={{
					px: { xs: 2, md: 3 },
					py: 1.5,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
					minHeight: 64,
				}}
			>
				{/* Left Section */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
					{/* Mobile Menu Toggle */}
					{isMobile && (
						<IconButton
							onClick={onMenuToggle}
							aria-label="Abrir menú"
							sx={{
								color: isDarkHeader ? "rgba(255,255,255,0.9)" : "#1a1a2e",
								backgroundColor: isDarkHeader
									? "rgba(255,255,255,0.1)"
									: "rgba(0,0,0,0.04)",
								"&:hover": {
									backgroundColor: isDarkHeader
										? "rgba(255,255,255,0.18)"
										: "rgba(0,0,0,0.08)",
								},
							}}
						>
							<MenuIcon />
						</IconButton>
					)}

					{/* Logo — non-logged mobile (more prominent) */}
					{!user && (
						<Box
							component="img"
							src={isDarkHeader ? logoViajesMolonesWhite : logoViajesMolones}
							alt="Viajes Molones"
							onClick={() => navigate("/")}
							sx={{
								display: { xs: "block", md: "none" },
								height: 36,
								width: "auto",
								cursor: "pointer",
								flexShrink: 0,
							}}
						/>
					)}

					{/* Logo — logged mobile */}
					{user && isMobile && (
						<Box
							component="img"
							src={isDarkHeader ? logoViajesMolonesWhite : logoViajesMolones}
							alt="Viajes Molones"
							onClick={() => navigate("/")}
							sx={{
								height: 28,
								width: "auto",
								cursor: "pointer",
								flexShrink: 0,
							}}
						/>
					)}

					{/* Search Icon — compact version for very small screens (logged users) */}
					{user && (
						<IconButton
							onClick={() => onSearchOpen?.()}
							aria-label="Buscar"
							sx={{
								display: { xs: "flex", sm: "none" },
								color: isDarkHeader ? "rgba(255,255,255,0.9)" : "#64748b",
								backgroundColor: isDarkHeader
									? "rgba(255,255,255,0.1)"
									: "rgba(0,0,0,0.04)",
								"&:hover": {
									backgroundColor: isDarkHeader
										? "rgba(255,255,255,0.18)"
										: "rgba(0,0,0,0.08)",
								},
							}}
						>
							<SearchIcon />
						</IconButton>
					)}

					{/* Search Bar — only for logged users (sm+), or desktop non-logged */}
					<Box
						onClick={() => onSearchOpen?.()}
						role="button"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") onSearchOpen?.();
						}}
						sx={{
							display: user
								? { xs: "none", sm: "flex" }
								: { xs: "none", md: "flex" },
							alignItems: "center",
							backgroundColor: "rgba(0, 0, 0, 0.04)",
							borderRadius: "14px",
							px: 2,
							py: 1.25,
							flex: 1,
							maxWidth: { xs: "100%", md: 480 },
							border: "2px solid transparent",
							transition: "all 0.2s ease",
							cursor: "pointer",
							userSelect: "none",
							"&:hover": {
								backgroundColor: "rgba(0, 0, 0, 0.06)",
								borderColor: "rgba(99, 102, 241, 0.2)",
							},
						}}
					>
						<SearchIcon
							sx={{
								color: "#8B8FA3",
								mr: 1.5,
								fontSize: 22,
							}}
						/>
						<Typography
							sx={{
								flex: 1,
								minWidth: 0,
								fontSize: "0.938rem",
								fontWeight: 500,
								color: "#8B8FA3",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								"& .placeholder-mobile": {
									display: { xs: "inline", md: "none" },
								},
								"& .placeholder-desktop": {
									display: { xs: "none", md: "inline" },
								},
							}}
						>
							<span className="placeholder-mobile">Buscar...</span>
							<span className="placeholder-desktop">
								{size.width < 1280
									? "Buscar..."
									: "Buscar guías, destinos, plantillas..."}
							</span>
						</Typography>
						<Box
							sx={{
								display: { xs: "none", md: "flex" },
								alignItems: "center",
								gap: 0.5,
								ml: 1,
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									px: 0.75,
									py: 0.25,
									borderRadius: "6px",
									backgroundColor: "rgba(0, 0, 0, 0.06)",
									fontSize: "0.75rem",
									fontWeight: 500,
									color: "#8B8FA3",
								}}
							>
								<KeyboardCommandKeyIcon sx={{ fontSize: 12, mr: 0.25 }} />K
							</Box>
						</Box>
					</Box>
				</Box>

				{/* Right Section */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{/* Create Button — only for logged users */}
					{user && (
						<Button
							data-nosnippet
							variant="contained"
							startIcon={<AddIcon />}
							onClick={onCreateNew}
							sx={{
								textTransform: "none",
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								fontWeight: 600,
								px: 2.5,
								py: 1,
								borderRadius: "12px",
								fontSize: "0.875rem",
								boxShadow: "none",
								display: { xs: "none", sm: "flex" },
								"&:hover": {
									background:
										"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
									boxShadow: "none",
									transform: "translateY(-1px)",
								},
								transition: "all 0.2s ease",
							}}
						>
							Crear guía
						</Button>
					)}

					{/* Mobile Create Button — only for logged users */}
					{user && (
						<IconButton
							onClick={onCreateNew}
							sx={{
								display: { xs: "flex", sm: "none" },
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								color: "#fff",
								boxShadow: "none",
								"&:hover": {
									background:
										"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
								},
							}}
						>
							<AddIcon />
						</IconButton>
					)}

					{/* Notifications */}
					{user && (
						<>
							<IconButton
								onClick={handleNotifClick}
								sx={{
									color: "#64748b",
									display: { xs: "none", md: "flex" },
									backgroundColor: "transparent",
									"&:hover": {
										backgroundColor: "rgba(0, 0, 0, 0.04)",
									},
								}}
							>
								<Badge
									badgeContent={unreadNotificationCount}
									invisible={unreadNotificationCount === 0}
									color="error"
									sx={{
										"& .MuiBadge-badge": {
											fontSize: 10,
											minWidth: 20,
											width: 20,
											height: 20,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											background:
												"linear-gradient(135deg, #F43F5E 0%, #E11D48 100%)",
											border: "2px solid #fff",
											color: "#fff",
										},
									}}
								>
									<NotificationsNoneOutlinedIcon sx={{ fontSize: 24 }} />
								</Badge>
							</IconButton>
							<Menu
								anchorEl={notifAnchorEl}
								open={notifOpen}
								onClose={handleNotifClose}
								anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
								transformOrigin={{ vertical: "top", horizontal: "right" }}
								PaperProps={{
									sx: {
										mt: 1.5,
										minWidth: 320,
										maxWidth: 380,
										maxHeight: 400,
										borderRadius: "16px",
										boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
										border: "1px solid rgba(0, 0, 0, 0.06)",
										overflow: "visible",
										"&::before": {
											content: '""',
											position: "absolute",
											top: -6,
											right: 20,
											width: 12,
											height: 12,
											backgroundColor: "#fff",
											transform: "rotate(45deg)",
											border: "1px solid rgba(0, 0, 0, 0.06)",
											borderBottom: "none",
											borderRight: "none",
										},
									},
								}}
							>
								<Box
									sx={{
										px: 2,
										py: 1.5,
										borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<Typography
											sx={{
												fontWeight: 600,
												color: "#1a1a2e",
												fontSize: "0.938rem",
											}}
										>
											Notificaciones
										</Typography>
										{notifications.length > 0 && (
											<Typography
												component="button"
												onClick={() => {
													dismissAllNotifications();
													handleNotifClose();
												}}
												sx={{
													fontSize: "0.75rem",
													color: "#6366F1",
													fontWeight: 600,
													background: "none",
													border: "none",
													cursor: "pointer",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												Marcar todas leídas
											</Typography>
										)}
									</Box>
								</Box>
								<Box sx={{ maxHeight: 320, overflowY: "auto" }}>
									{notifications.length === 0 ? (
										<Box sx={{ px: 2, py: 4, textAlign: "center" }}>
											<NotificationsNoneOutlinedIcon
												sx={{ fontSize: 40, color: "#D1D5DB", mb: 1 }}
											/>
											<Typography
												sx={{ color: "#8B8FA3", fontSize: "0.875rem" }}
											>
												No tienes notificaciones
											</Typography>
										</Box>
									) : (
										notifications.map((n) => (
											<Box
												key={n.id}
												sx={{
													px: 2,
													py: 1.5,
													pb: 2,
													borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
													"&:last-child": { borderBottom: "none" },
												}}
											>
												<Box
													sx={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "flex-start",
														gap: 1,
													}}
												>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography
															sx={{
																fontWeight: 600,
																color: "#1a1a2e",
																fontSize: "0.875rem",
															}}
														>
															{n.title}
														</Typography>
														<Typography
															sx={{
																color: "#8B8FA3",
																fontSize: "0.813rem",
																mt: 0.25,
															}}
														>
															{n.message}
														</Typography>
														{n.action && (
															<Box
																component="button"
																onClick={() => handleNotificationAction(n)}
																sx={{
																	mt: 1,
																	display: "inline-block",
																	background: "none",
																	border: "none",
																	padding: 0,
																	cursor: "pointer",
																	fontFamily: "inherit",
																	fontWeight: 600,
																	fontSize: "0.813rem",
																	color: "#6366F1",
																	borderRadius: "8px",
																	px: 1,
																	py: 0.5,
																	"&:hover": {
																		backgroundColor: "rgba(99, 102, 241, 0.08)",
																	},
																}}
															>
																{n.action.label}
															</Box>
														)}
													</Box>
													<IconButton
														size="small"
														onClick={() => dismissNotification(n.id)}
														sx={{
															color: "#9CA3AF",
															p: 0.5,
															"&:hover": {
																color: "#EF4444",
																backgroundColor: "rgba(239, 68, 68, 0.08)",
															},
														}}
													>
														<Box component="span" sx={{ fontSize: 14 }}>
															×
														</Box>
													</IconButton>
												</Box>
											</Box>
										))
									)}
								</Box>
							</Menu>
						</>
					)}

					{/* Auth Buttons / User Menu */}
					{!user ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: { xs: 0.5, sm: 1 },
							}}
						>
							<Button
								variant="text"
								onClick={onLogin}
								sx={{
									textTransform: "none",
									color: isDarkHeader
										? "#fff !important"
										: "#4B5563 !important",
									fontWeight: 600,
									fontSize: { xs: "0.813rem", sm: "0.875rem" },
									borderRadius: "10px",
									px: { xs: 1.25, sm: 2 },
									py: { xs: 0.75, sm: 1 },
									minWidth: "auto",
									"&:hover": {
										backgroundColor: isDarkHeader
											? "rgba(255,255,255,0.12)"
											: "rgba(0, 0, 0, 0.04)",
										color: isDarkHeader
											? "#fff !important"
											: "#6366F1 !important",
									},
								}}
							>
								Entrar
							</Button>
							<Box
								onClick={onSignUp}
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									cursor: "pointer",
									transition: "all 0.2s ease",
									"&:hover .signup-btn": {
										background:
											"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
									},
								}}
							>
								<Box
									className="signup-btn"
									sx={{
										textTransform: "none",
										fontWeight: 600,
										fontSize: { xs: "0.813rem", sm: "0.875rem" },
										borderRadius: "10px",
										px: { xs: 1.75, sm: 2.5 },
										py: { xs: 0.75, sm: 1 },
										background:
											"linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
										color: "#fff",
										transition: "all 0.2s ease",
										textAlign: "center",
									}}
								>
									Registrarse
								</Box>
							</Box>
						</Box>
					) : (
						<>
							{/* User Avatar Button */}
							<Box
								onClick={handleMenuClick}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.5,
									cursor: "pointer",
									pl: 0.5,
									pr: 1.5,
									py: 0.5,
									borderRadius: "12px",
									transition: "all 0.15s ease",
									border: "1px solid transparent",
									"&:hover": {
										backgroundColor: "rgba(0, 0, 0, 0.04)",
										border: "1px solid rgba(0, 0, 0, 0.06)",
									},
								}}
							>
								<Avatar
									sx={{
										width: 36,
										height: 36,
										background:
											"linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
										fontSize: "0.875rem",
										fontWeight: 600,
										boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
									}}
								>
									{user.displayName
										? user.displayName.charAt(0).toUpperCase()
										: user.email?.charAt(0).toUpperCase() || "U"}
								</Avatar>
								<Box sx={{ display: { xs: "none", md: "block" } }}>
									<Typography
										sx={{
											fontWeight: 600,
											color: "#1a1a2e",
											fontSize: "0.875rem",
											lineHeight: 1.2,
										}}
									>
										{user.displayName || "Usuario"}
									</Typography>
									<Typography
										sx={{
											fontSize: "0.75rem",
											color: isPro ? "#6366F1" : "#8B8FA3",
											fontWeight: isPro ? 600 : 400,
										}}
									>
										{isPro ? "Plan Pro" : "Plan gratuito"}
									</Typography>
								</Box>
							</Box>

							{/* User Menu */}
							<Menu
								anchorEl={anchorEl}
								open={open}
								onClose={handleMenuClose}
								anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
								transformOrigin={{ vertical: "top", horizontal: "right" }}
								PaperProps={{
									sx: {
										mt: 1.5,
										minWidth: 260,
										borderRadius: "16px",
										boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
										border: "1px solid rgba(0, 0, 0, 0.06)",
										overflow: "visible",
										"&::before": {
											content: '""',
											position: "absolute",
											top: -6,
											right: 20,
											width: 12,
											height: 12,
											backgroundColor: "#fff",
											transform: "rotate(45deg)",
											border: "1px solid rgba(0, 0, 0, 0.06)",
											borderBottom: "none",
											borderRight: "none",
										},
									},
								}}
							>
								{/* User Info Header */}
								<Box
									sx={{
										px: 2.5,
										py: 2,
										backgroundColor: "rgba(0, 0, 0, 0.02)",
									}}
								>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
										<Avatar
											sx={{
												width: 48,
												height: 48,
												background:
													"linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
												fontSize: "1.125rem",
												fontWeight: 600,
											}}
										>
											{user.displayName
												? user.displayName.charAt(0).toUpperCase()
												: user.email?.charAt(0).toUpperCase() || "U"}
										</Avatar>
										<Box>
											<Typography
												sx={{
													fontWeight: 600,
													color: "#1a1a2e",
													fontSize: "0.938rem",
												}}
											>
												{user.displayName || "Usuario"}
											</Typography>
											<Typography
												sx={{ color: "#8B8FA3", fontSize: "0.813rem" }}
											>
												{user.email}
											</Typography>
										</Box>
									</Box>
									{/* Upgrade Banner (solo si no es Pro) / Gestionar suscripción (si es Pro) */}
									<Box
										onClick={async () => {
											if (portalLoading) return;
											if (isPro && openCustomerPortal) {
												setPortalLoading(true);
												handleMenuClose();
												try {
													await openCustomerPortal();
												} catch {
													setPortalLoading(false);
												}
											} else if (!isPro) {
												handleMenuClose();
												navigate("/profile", { state: { section: "plan" } });
											}
										}}
										sx={{
											mt: 2,
											p: 1.5,
											borderRadius: "10px",
											background:
												"linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)",
											border: "1px solid rgba(99, 102, 241, 0.2)",
											display: "flex",
											alignItems: "center",
											gap: 1,
											cursor: "pointer",
											"&:hover": {
												background:
													"linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)",
											},
										}}
									>
										<DiamondOutlinedIcon
											sx={{ color: "#6366F1", fontSize: 20 }}
										/>
										<Box sx={{ flex: 1 }}>
											<Typography
												sx={{
													fontSize: "0.813rem",
													fontWeight: 600,
													color: "#1a1a2e",
												}}
											>
												{isPro ? "Gestionar suscripción" : "Actualizar a Pro"}
											</Typography>
											<Typography
												sx={{ fontSize: "0.75rem", color: "#8B8FA3" }}
											>
												{isPro
													? "Facturación y renovación"
													: "Guías ilimitadas y más"}
											</Typography>
										</Box>
										{!isPro && (
											<Chip
												label="50% OFF"
												size="small"
												sx={{
													height: 22,
													fontSize: "0.688rem",
													fontWeight: 700,
													background:
														"linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
													color: "#fff",
												}}
											/>
										)}
									</Box>
								</Box>

								<Divider sx={{ borderColor: "rgba(0, 0, 0, 0.06)" }} />

								{/* Menu Items */}
								<Box sx={{ py: 1 }}>
									<MenuItem
										onClick={handleNavigateToProfile}
										sx={{
											py: 1.25,
											px: 2.5,
											mx: 1,
											borderRadius: "8px",
											"&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
										}}
									>
										<PersonOutlineIcon
											sx={{ mr: 1.5, fontSize: 20, color: "#64748b" }}
										/>
										<Typography
											sx={{
												fontSize: "0.875rem",
												fontWeight: 500,
												color: "#4B5563",
											}}
										>
											Mi perfil
										</Typography>
									</MenuItem>
									<MenuItem
										onClick={() => {
											handleMenuClose();
											document
												.getElementById("my-guides-section")
												?.scrollIntoView({ behavior: "smooth" });
										}}
										sx={{
											py: 1.25,
											px: 2.5,
											mx: 1,
											borderRadius: "8px",
											"&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
										}}
									>
										<FolderOutlinedIcon
											sx={{ mr: 1.5, fontSize: 20, color: "#64748b" }}
										/>
										<Typography
											sx={{
												fontSize: "0.875rem",
												fontWeight: 500,
												color: "#4B5563",
											}}
										>
											Mis guías
										</Typography>
									</MenuItem>
									<MenuItem
										onClick={handleNavigateToSettings}
										sx={{
											py: 1.25,
											px: 2.5,
											mx: 1,
											borderRadius: "8px",
											"&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
										}}
									>
										<SettingsOutlinedIcon
											sx={{ mr: 1.5, fontSize: 20, color: "#64748b" }}
										/>
										<Typography
											sx={{
												fontSize: "0.875rem",
												fontWeight: 500,
												color: "#4B5563",
											}}
										>
											Configuración
										</Typography>
									</MenuItem>
								</Box>

								<Divider sx={{ borderColor: "rgba(0, 0, 0, 0.06)" }} />

								<Box sx={{ py: 1 }}>
									<MenuItem
										onClick={() => {
											handleMenuClose();
											onLogout?.();
										}}
										sx={{
											py: 1.25,
											px: 2.5,
											mx: 1,
											borderRadius: "8px",
											"&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" },
										}}
									>
										<LogoutIcon
											sx={{ mr: 1.5, fontSize: 20, color: "#EF4444" }}
										/>
										<Typography
											sx={{
												fontSize: "0.875rem",
												fontWeight: 500,
												color: "#EF4444",
											}}
										>
											Cerrar sesión
										</Typography>
									</MenuItem>
								</Box>
							</Menu>
						</>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default Topbar;
